"use client";

import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { qaApi } from "@/lib/api/services/qa.api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { HelpCircle, Plus, MessageSquare, ArrowUp } from "lucide-react";
import Link from "next/link";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/empty-state";

export default function QAListPage() {
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ["qa-list", page],
    queryFn: async () => {
      const result = await qaApi.list({ page, perPage: 20 });
      const items = ((result as unknown as { data?: Array<Record<string, unknown>> }).data ?? []) as Array<{
        id: string;
        title: string;
        description?: string;
        tags?: string[];
        author?: string;
        createdAt?: string;
        answerCount?: number;
        upvoteCount?: number;
      }>;
      return {
        questions: items,
        meta: (result as { meta?: { total: number; totalPages: number } }).meta ?? { total: 0, totalPages: 1 },
      };
    },
  });

  const questions = data?.questions ?? [];
  const meta = data?.meta ?? { total: 0, totalPages: 1 };

  return (
    <main className="container mx-auto max-w-7xl px-4 py-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Questions & Answers</h1>
          <p className="mt-1 text-sm text-muted-foreground">Ask questions and get answers from the HR community.</p>
        </div>
        <Button asChild>
          <Link href="/qa/ask">
            <Plus className="mr-2 h-4 w-4" /> Ask a Question
          </Link>
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i}>
              <CardContent className="p-5">
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="mt-2 h-4 w-1/2" />
                <div className="mt-3 flex gap-2">
                  <Skeleton className="h-5 w-16" />
                  <Skeleton className="h-5 w-20" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : questions.length === 0 ? (
        <EmptyState icon={HelpCircle} description="No questions yet. Be the first to ask!" />
      ) : (
        <div className="space-y-4">
          {questions.map((q) => (
            <Link key={q.id} href={`/qa/${q.id}`}>
              <Card className="transition-colors hover:bg-muted/30">
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="flex flex-col items-center gap-1 text-xs text-muted-foreground">
                      <ArrowUp className="h-4 w-4" />
                      <span className="font-medium">{q.upvoteCount ?? 0}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading font-semibold text-foreground">{q.title}</h3>
                      {q.description && (
                        <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{q.description}</p>
                      )}
                      <div className="mt-3 flex flex-wrap gap-2">
                        {(q.tags ?? []).map((tag) => (
                          <Badge key={tag} variant="secondary" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                      <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <MessageSquare className="h-3.5 w-3.5" />
                          {q.answerCount ?? 0} answers
                        </span>
                        {q.createdAt && <span>{q.createdAt}</span>}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {meta.totalPages > 1 && (
        <div className="mt-8 flex justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </Button>
          <span className="flex items-center px-4 text-sm text-muted-foreground">
            Page {page} of {meta.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= meta.totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </main>
  );
}
